import React from 'react';

import { Provider } from 'react-redux';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { ThemeProvider } from 'styled-components';
import { CacheProvider } from '@emotion/react';
import store from '../redux/store';
import createEmotionCache from './createEmotionCache';

export default function PageWrapper({ theme, children, PageWrapperProps }) {
    const emotionCache = PageWrapperProps?.emotionCache ?? createEmotionCache();
    return (
        <ErrorBoundary>
            {/* CacheProvider injects the emotion styles in lieu of StyledEngineProvider */}
            <CacheProvider value={emotionCache}>
                <MuiThemeProvider theme={theme}>
                    <ThemeProvider theme={theme}>
                        <Provider store={store}>
                            {children}
                        </Provider>
                    </ThemeProvider>
                </MuiThemeProvider>
            </CacheProvider>
        </ErrorBoundary>
    );
}

/**
 * React error boundary. Catches exceptions thrown during render, in
 * lifecycle methods, and in constructors of the children tree. Without
 * this, an unhandled exception white-screens the whole app — surfaced
 * 2026-05-14 during the Bill Kinney walkthrough when a malformed
 * SMARTS input crashed the chat panel and took the app with it.
 *
 * Exported as a named export so individual high-risk surfaces (chat,
 * workflow, results) can wrap themselves with their own boundary, so
 * one surface crashing doesn't take down the others.
 *
 * Usage:
 *   <ErrorBoundary surfaceName="ChatBot"><MyComponent /></ErrorBoundary>
 *
 * @typedef {Object} ErrorBoundaryProps
 * @property {React.ReactNode} children
 * @property {string} [surfaceName] - displayed in the fallback UI
 * @property {React.ReactNode} [fallback] - custom fallback content
 */
export class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        const { surfaceName } = this.props;
        console.error(
            `[ErrorBoundary${surfaceName ? `: ${surfaceName}` : ''}] caught:`,
            error,
        );
        console.error('componentStack:', errorInfo?.componentStack);
    }

    handleReset = () => {
        this.setState({ hasError: false, error: null });
    };

    render() {
        const { children, surfaceName, fallback } = this.props;
        const { hasError, error } = this.state;
        if (!hasError) return children;
        if (fallback) return fallback;
        // Say what actually failed. Without a surfaceName this boundary is whatever it was
        // mounted around -- at the PageWrapper level that is the whole app, so calling it
        // "this panel" tells the user the damage is smaller than it is.
        const label = surfaceName ? `${surfaceName} stopped working.` : 'BMaps hit an unexpected error.';
        const message = error?.message || String(error || 'Unknown error');
        return (
            <div style={{
                padding: '1rem',
                margin: '0.5rem',
                border: '1px solid #f0c8c8',
                borderRadius: 4,
                background: '#fff5f5',
                color: '#7a2b2b',
                fontSize: '0.875rem',
                lineHeight: 1.4,
            }}
            >
                <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>
                    {label}
                </div>
                <div style={{
                    marginBottom: '0.5rem',
                    fontFamily: 'monospace',
                    fontSize: '0.8rem',
                    background: '#fff',
                    border: '1px solid #ead0d0',
                    padding: '0.5rem',
                    borderRadius: 2,
                    wordBreak: 'break-word',
                }}
                >
                    {message}
                </div>
                <div style={{ marginBottom: '0.5rem' }}>
                    Try again re-renders without reloading — if the cause is still there it
                    will fail again. Reloading restarts BMaps and discards unsaved work.
                </div>
                <button
                    type="button"
                    onClick={this.handleReset}
                    style={{
                        padding: '0.4rem 0.8rem',
                        border: '1px solid #c98080',
                        background: '#fff',
                        borderRadius: 3,
                        cursor: 'pointer',
                        marginRight: '0.5rem',
                    }}
                >
                    Try again
                </button>
                <button
                    type="button"
                    onClick={() => window.location.reload()}
                    style={{
                        padding: '0.4rem 0.8rem',
                        border: '1px solid #c98080',
                        background: '#fff',
                        borderRadius: 3,
                        cursor: 'pointer',
                    }}
                >
                    Reload BMaps
                </button>
            </div>
        );
    }
}
